import {
  Divider,
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateView, PickersDayProps } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar, DateCalendarSlotsComponentsProps } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  compareAsc,
  format,
  isSameDay
} from "date-fns";
import it from "date-fns/locale/it";
import { ChevronLeft, ChevronRight, LucideWandSparkles } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarContainerStack,
  CalendarSmartDialog,
  CalendarSmartDrawer,
  DaySpan,
  DotSpan,
  SmartLearningCreateButton,
  StyledPickersDay
} from "./CalendarCard.style";
import { SmartLearningModalContent } from "./SmartLearningModalContent.component";
import { useAuthStore, useLanguageStore } from "../../stores";
import type { CalendarDateObject } from "../../types";
import {
  useDayColor,
  withPickersDayProps
} from "../../utils/calendar";
import { switchLocaleImport } from "../../utils/dateFnsLocale";

export function CalendarCard({
  dateObjects = [],
  dateNumbersToHighlight,
  selectedExternal,
  setSelectedExternal,
  setSelectedMonth,
  setSelectedYear
}: {
  dateObjects?: CalendarDateObject[]
  dateNumbersToHighlight: number[]
  selectedExternal: Date
  setSelectedExternal: Dispatch<SetStateAction<Date>>
  setSelectedMonth: Dispatch<SetStateAction<number>>
  setSelectedYear: Dispatch<SetStateAction<number>>
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const smartLearning = useAuthStore(state => state.smartConfiguration?.smartLearning);
  const currentDay = new Date();
  const [selected, setSelected] = useState<Date>(selectedExternal);
  const [view, setView] = useState<DateView>("day");

  // const [daysToHighlightState, setDaysToHighlightState] = useState<number[]>([]);
  // const [dateObjectsState, setDateObjectsState] = useState<CalendarDateObject[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const language = useLanguageStore(state => state.language)?.id;

  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch(err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);

  useEffect(() => {
    const buttons = document.querySelectorAll(
      ".MuiPickersCalendarHeader-labelContainer button"
    );

    buttons.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) {
        btn.id = "calendar-switch-view-btn";
      }
    });
  }, []);

  const stringDay = format(currentDay, "EEEE", { locale: locale });
  const numberDay = format(currentDay, "dd", { locale: locale });
  const currentMonth = format(selected, "MMMM", { locale: locale });
  const labelSwitchView =`${currentMonth} - ${t("calendar_switch_view",{ view:view === "day" ? t("month"):t("year"),view_to_show:view !== "day" ? t("month"):t("year") })}`;

  return (
    <>
      <CalendarContainerStack aria-label={ `${stringDay} ${numberDay}` }>
        <Stack direction="row">
          <LocalizationProvider
            dateAdapter={ AdapterDateFns }
            adapterLocale={ locale }>
            <DateCalendar
              view={ view }
              onViewChange={ (newView) => setView(newView) }
              disableHighlightToday={ true }
              value={ selected }
              onChange={ (newDate) => {
                newDate?.setHours(0, 0, 0);
                setSelected(newDate ?? new Date());
                setSelectedExternal(newDate ?? new Date());
              } }
              onMonthChange={ (month) => {
                month.setHours(0, 0, 0);
                setSelected(month);
                setSelectedExternal(month);
                setSelectedMonth(parseInt(format(month, "MM")));
                setSelectedYear(parseInt(format(month, "yyyy")));
              } }
              onYearChange={ (year) => {
                year.setHours(0, 0, 0);
                setSelected(year);
                setSelectedExternal(year);
                setSelectedYear(parseInt(format(year, "yyyy")));
              } }
              slots={ {
                day: SeededCalendarCardDay({ dateNumbersToHighlight, dateObjects, setSelected }),
                leftArrowIcon:
                () => <ChevronLeft color={ theme.customColors.exploreCardText }  />,
                rightArrowIcon:
                () => <ChevronRight color={ theme.customColors.exploreCardText } />
              } }
              slotProps={ {
                nextIconButton: {
                  "aria-label": t("calendar_next_month"),
                  "title": t("calendar_next_month")
                },
                previousIconButton: {
                  "aria-label": t("calendar_prev_month"),
                  "title": t("calendar_prev_month")
                },
                switchViewButton: {
                  "aria-label": labelSwitchView,
                  "tabIndex": "0"
                }
              } as unknown as DateCalendarSlotsComponentsProps<Date> }
              showDaysOutsideCurrentMonth={ false }
            />
          </LocalizationProvider>
        </Stack>

        <Divider/>

        { smartLearning ? (
          <Stack direction={ "column" } alignItems={ "start" } gap={ 3 }>
            <Stack direction={ "row" } alignItems={ "center" } gap={ "8px" }>
              <LucideWandSparkles aria-hidden="true"/>
              <Typography variant={ "h4" } fontSize={ "16px" } fontWeight={ "bold" }>{ t("smart_learning") }</Typography>
            </Stack>
            <Stack width={ "100%" } direction={ "row" } alignItems={ "center" } justifyContent={ "space-between" }>
              <Typography fontSize={ "14px" }>{ t("plan_your_study") }</Typography>
              <SmartLearningCreateButton
                id={ "smart_learning_button" }
                type={ "button" }
                typology={ "secondary" }
                size={ "small" }
                onClick={ () => setIsOpen(true) }
              >
                <Typography fontSize={ "14px" } color={ theme.customColors.exploreCardText }>
                  { t("create_slot") }
                </Typography>
              </SmartLearningCreateButton>
            </Stack>
          </Stack>
        ) : <div/> }
        { isMobile ?
          <CalendarSmartDrawer
            anchor={ "bottom" }
            open={ isOpen }
            disablePortal={ true }
            onClose={ () => setIsOpen(false) }
            onOpen={ () => setIsOpen(true) }
            className={ "smart-drawer" }
          >
            <SmartLearningModalContent
              onClose={ () => setIsOpen(false) }
              selectedDate={ selected }
            />
          </CalendarSmartDrawer> :
          <CalendarSmartDialog
            // il titolo ("title-to-announce") è reso da SmartLearningModalContent in tutti i suoi stati
            aria-labelledby="title-to-announce"
            maxWidth={ false }
            open={ isOpen }
            onClose={ (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
              if (reason == "backdropClick" || reason == "escapeKeyDown") {
                return;
              }
              setIsOpen(false);
            } }
            scroll={ "paper" }
          >
            <SmartLearningModalContent
              onClose={ () => setIsOpen(false) }
              selectedDate={ selected }
            />
          </CalendarSmartDialog> }
      </CalendarContainerStack>
    </>
  );
}

function CalendarCardDay({
  dateObjects,
  day,
  dateNumbersToHighlight,
  outsideCurrentMonth,
  selected,
  setSelected: _setSelected,
  ...props
}: PickersDayProps<Date> & {
  dateObjects: CalendarDateObject[]
  setSelected?: Dispatch<SetStateAction<Date>>
  dateNumbersToHighlight: number[]
}) {
  const { t } = useTranslation();
  let thisDayCourses: CalendarDateObject[] = [];
  if (dateObjects.length > 0) {
    thisDayCourses = dateObjects.filter((dateObject) => {
      const courseDate = format(new Date(dateObject.date.join("/")), "yyyy/MM/dd");
      const thisDayDate = format(day, "yyyy/MM/dd");
      const compare = compareAsc(new Date(thisDayDate), new Date(courseDate)) == 0;
      return compare;
    });
  }
  const activityColor = useDayColor(thisDayCourses);

  const currentDay = new Date();

  const language = useLanguageStore(state => state.language)?.id;

  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch(err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);


  return <StyledPickersDay
    aria-label={ `${format(day, "EEEE, dd MMMM yyyy", { locale })}, ${(selected ? t("selected") : t("not_selected")).toLowerCase()}` }
    outsideCurrentMonth={ outsideCurrentMonth }
    day={ day }
    activityColor={ activityColor }
    $sameDay={ isSameDay(day, currentDay) }
    $selected={ selected ?? false }
    { ...props }
  >
    <DaySpan>
      { format(day, "d", { locale: locale }) }
    </DaySpan>
    { /* (
      thisDayCourses.length > 0
        && !outsideCurrentMonth
        && !selected
    ) ? <DotSpan /> : null
    */ }
    {
      (
        dateNumbersToHighlight
        && dateNumbersToHighlight.includes(day.getDate())
        && !outsideCurrentMonth
        && !selected
      ) ? <DotSpan /> : null
    }
  </StyledPickersDay>;
}

/**
 * SeededCalendarDay is a "useless" wrapper, meant to appease typescript and the mui calendar prop types
 * @param CalendarDay props
 * @returns a CalendarDay component, typed with additional mui calendar props
 */
const SeededCalendarCardDay = (props: {
  dateObjects: CalendarDateObject[],
  dateNumbersToHighlight: number[],
  setSelected: Dispatch<SetStateAction<Date>>
}) => withPickersDayProps<{
  dateObjects: CalendarDateObject[]
  dateNumbersToHighlight: number[]
  setSelected: Dispatch<SetStateAction<Date>>
}>({
  component: CalendarCardDay,
  ...props
});
